import React, { useState } from 'react';
import {
  Bell,
  MessageCircle,
  Mail,
  CheckCircle2,
  Send,
  ExternalLink,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { usePropertyAlert } from '../context/PropertyAlertContext';

interface PropertyAlertInlineBoxProps {
  city?: string;
  quarter?: string;
  category?: string;
  className?: string;
}

export const PropertyAlertInlineBox: React.FC<PropertyAlertInlineBoxProps> = ({
  city = 'Douala',
  quarter = '',
  category = 'all',
  className = '',
}) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const { openAlertModal } = usePropertyAlert();

  const [inputVal, setInputVal] = useState('');
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waLink, setWaLink] = useState('');

  const locLabel = quarter ? `${city} (${quarter})` : city;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanInput = inputVal.trim();
    if (!cleanInput || submitting) return;

    setSubmitting(true);
    try {
      const isEmail = cleanInput.includes('@');
      const params = {
        channel: isEmail ? ('email' as const) : ('whatsapp' as const),
        whatsappPhone: isEmail ? undefined : cleanInput,
        phone: isEmail ? undefined : cleanInput,
        email: isEmail ? cleanInput : undefined,
        city,
        quarter,
        category,
        titreFoncierOnly: true,
        language: isFr ? 'fr' : 'en',
        source: 'inline_box',
      };

      const { subscribeListingAlert, buildWhatsAppAlertActivationUrl } = await import('../lib/firestoreService');
      await subscribeListingAlert(params);
      const url = buildWhatsAppAlertActivationUrl(params);
      setWaLink(url);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to register inline alert:', err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`bg-[#094426] text-white rounded-2xl p-6 sm:p-8 border border-[#0d5932] shadow-sm ${className}`}
    >
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Subtle Institutional Kicker */}
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-200">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span>
            {isFr
              ? `Alerte Foncière & Immobilière • ${locLabel}`
              : `Property Alerts • ${locLabel}`}
          </span>
        </div>

        {/* Clear, Human Headline & Subhead */}
        <div>
          <h3 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight leading-snug">
            {isFr
              ? `Soyez averti dès qu'un bien avec Titre Foncier est publié à ${locLabel}`
              : `Get notified when verified titled properties are listed in ${locLabel}`}
          </h3>
          <p className="text-xs sm:text-sm text-emerald-100/85 mt-1.5 leading-relaxed">
            {isFr
              ? 'Recevez les nouveaux terrains et biens certifiés directement sur WhatsApp ou par email dès leur mise en ligne.'
              : 'Direct priority notification via WhatsApp or email whenever a verified title deed listing arrives in this location.'}
          </p>
        </div>

        {/* Form or Success State */}
        {submitted ? (
          <div className="bg-white/10 border border-white/20 rounded-xl p-4 sm:p-5 text-center space-y-3 animate-in fade-in duration-200">
            <div className="inline-flex items-center gap-2 text-emerald-300 font-bold text-sm">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span>{isFr ? 'Alerte enregistrée avec succès !' : 'Alert successfully configured!'}</span>
            </div>
            <p className="text-xs text-white/80 max-w-md mx-auto">
              {isFr
                ? 'Pour recevoir vos fiches techniques directement sur votre smartphone, confirmez en 1 clic sur WhatsApp :'
                : 'To receive property dossiers directly on your smartphone, confirm with 1 click on WhatsApp:'}
            </p>
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#16A34A] hover:bg-[#15803D] text-white text-xs font-bold shadow-xs transition-colors"
              >
                <MessageCircle className="w-4 h-4 fill-white" />
                <span>{isFr ? 'Confirmer sur WhatsApp (1-Clic)' : 'Confirm on WhatsApp (1-Click)'}</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            {/* Clean Segmented Channel Toggle */}
            <div className="inline-flex p-1 bg-black/25 rounded-xl border border-white/10 gap-1">
              <button
                type="button"
                onClick={() => setChannel('whatsapp')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer ${
                  channel === 'whatsapp'
                    ? 'bg-[#16A34A] text-white shadow-xs'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5 fill-white" />
                <span>WhatsApp</span>
              </button>
              <button
                type="button"
                onClick={() => setChannel('email')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer ${
                  channel === 'email'
                    ? 'bg-white text-[#094426] shadow-xs'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Email</span>
              </button>
            </div>

            {/* Input & Action */}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <input
                  type={channel === 'whatsapp' ? 'tel' : 'email'}
                  required
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  placeholder={
                    channel === 'whatsapp'
                      ? isFr
                        ? 'Numéro WhatsApp (ex: 677 23 45 67)'
                        : 'WhatsApp number (e.g. 677 23 45 67)'
                      : isFr
                      ? 'Votre adresse email'
                      : 'Your email address'
                  }
                  className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs shrink-0 transition-colors flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5 text-slate-950" />
                <span>
                  {submitting
                    ? isFr
                      ? 'Enregistrement...'
                      : 'Saving...'
                    : isFr
                    ? 'Créer mon Alerte'
                    : 'Create Alert'}
                </span>
              </button>
            </form>

            {/* Practical Controls & Trust */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1 text-[11px] text-emerald-200/80">
              <button
                type="button"
                onClick={() => openAlertModal({ city, quarter, category })}
                className="hover:text-amber-300 transition-colors flex items-center gap-1 cursor-pointer font-medium self-start sm:self-auto underline decoration-emerald-400/40 hover:decoration-amber-300"
              >
                <SlidersHorizontal className="w-3 h-3" />
                <span>
                  {isFr
                    ? 'Ajuster les critères (Budget, Titre Foncier...)'
                    : 'Refine criteria (Budget, Title Deed...)'}
                </span>
              </button>

              <div className="flex items-center gap-1.5 text-[11px] text-emerald-100/70">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>
                  {isFr
                    ? 'Gratuit • Désinscription libre à tout moment'
                    : '100% Free • Unsubscribe anytime'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
